import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import type { TrackReferenceOrPlaceholder } from "@livekit/components-react"

import { sourceToViewport, type FrameGeometry, type ViewportRect, type ViewportTransform } from "../coordinates/sourceToViewport"
import type { SyncFrameDescriptorPayload } from "../envelope"
import { PointerLayer } from "../pointer/PointerLayer"
import { useRoomSync } from "../RoomSyncProvider"
import { displayGeometry, isMirroredTransform, liveGeometry, relativeRect } from "./stageGeometry"

/**
 * SyncStageOverlay - the one place that measures the stage and builds the per-viewer coordinate
 * transform (D-05, D-07, RS-09/RS-10/RS-11). Mounted inside Phase 4's `div.rs-stage`, it renders
 * the shared frozen frame when the room is frozen, and always exposes exactly one
 * `sourceToViewport` transform to its children (the pointer layer here, the annotation canvas in
 * 05-15) so pointer, image and shapes stay aligned by construction.
 */

export type StageSurface = {
  transform: ViewportTransform
  width: number
  height: number
  source: { kind: "track" | "frame"; id: string }
  frozen: boolean
  frameId: string | null
}

type Measurement = {
  overlayWidth: number
  overlayHeight: number
  videoRect: ViewportRect | null
  videoW: number
  videoH: number
  mirrored: boolean
}

const EMPTY_MEASUREMENT: Measurement = {
  overlayWidth: 0,
  overlayHeight: 0,
  videoRect: null,
  videoW: 0,
  videoH: 0,
  mirrored: false,
}

export function SyncStageOverlay(p: {
  stageTrack: TrackReferenceOrPlaceholder | undefined
  interaction: "pointer" | "draw" | "none"
  children?: (surface: StageSurface) => ReactNode
}) {
  const sync = useRoomSync()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const videoElRef = useRef<HTMLVideoElement | null>(null)
  const videoObserverRef = useRef<ResizeObserver | null>(null)
  const [measurement, setMeasurement] = useState<Measurement>(EMPTY_MEASUREMENT)

  const measure = useCallback(() => {
    const root = rootRef.current
    if (!root) {
      return
    }
    const overlayRect = root.getBoundingClientRect()
    // StageSyncLayer wraps the overlay AND the toolbar in a `display: contents` div, so
    // root.parentElement is that wrapper (which holds no <video>), not the stage. Walk to the
    // stage element that actually contains the video instead of assuming the direct parent.
    const stage = root.closest(".rs-stage")
    const video = stage?.querySelector("video") ?? null

    if (video !== videoElRef.current) {
      videoObserverRef.current?.disconnect()
      videoObserverRef.current = null
      videoElRef.current?.removeEventListener("loadedmetadata", measure)
      videoElRef.current?.removeEventListener("resize", measure)
      videoElRef.current = video
      if (video) {
        video.addEventListener("loadedmetadata", measure)
        video.addEventListener("resize", measure)
        const ro = new ResizeObserver(() => measure())
        ro.observe(video)
        videoObserverRef.current = ro
      }
    }

    if (!video) {
      setMeasurement(EMPTY_MEASUREMENT)
      return
    }

    setMeasurement({
      overlayWidth: overlayRect.width,
      overlayHeight: overlayRect.height,
      videoRect: relativeRect(video.getBoundingClientRect(), overlayRect),
      videoW: video.videoWidth,
      videoH: video.videoHeight,
      mirrored: isMirroredTransform(getComputedStyle(video).transform),
    })
  }, [])

  // Re-query when the pinned stage track changes.
  useEffect(() => {
    measure()
  }, [measure, p.stageTrack])

  // Overlay resize, and a MutationObserver on the parent for when FocusLayout replaces the video
  // element outright (e.g. switching from a placeholder to a real <video>).
  useEffect(() => {
    const root = rootRef.current
    if (!root) {
      return
    }
    const ro = new ResizeObserver(() => measure())
    ro.observe(root)
    let mo: MutationObserver | null = null
    // Same wrapper caveat as `measure`: watch the stage element itself, not the `contents` wrapper.
    const mutationTarget = root.closest(".rs-stage") ?? root.parentElement
    if (mutationTarget) {
      mo = new MutationObserver(() => measure())
      mo.observe(mutationTarget, { childList: true, subtree: true })
    }
    return () => {
      ro.disconnect()
      mo?.disconnect()
      videoObserverRef.current?.disconnect()
      videoObserverRef.current = null
      if (videoElRef.current) {
        videoElRef.current.removeEventListener("loadedmetadata", measure)
        videoElRef.current.removeEventListener("resize", measure)
        videoElRef.current = null
      }
    }
  }, [measure])

  const room = sync.room
  const frozenFrame = room?.activeFrame ?? null

  let geometry: FrameGeometry | null
  let viewport: ViewportRect
  let source: { kind: "track" | "frame"; id: string }
  let frameId: string | null = null
  let hasSource: boolean

  if (frozenFrame) {
    geometry = displayGeometry(frozenFrame, frozenFrame.sourceIdentity === sync.localIdentity)
    viewport = { x: 0, y: 0, width: measurement.overlayWidth, height: measurement.overlayHeight }
    source = { kind: "frame", id: frozenFrame.frameId }
    frameId = frozenFrame.frameId
    hasSource = true
  } else {
    geometry = liveGeometry(measurement.videoW, measurement.videoH, measurement.mirrored)
    viewport = measurement.videoRect ?? { x: 0, y: 0, width: 0, height: 0 }
    const trackSid = p.stageTrack?.publication?.trackSid ?? null
    source = { kind: "track", id: trackSid ?? "" }
    hasSource = trackSid !== null
  }

  const transform = geometry ? sourceToViewport(geometry, viewport) : null
  const surface: StageSurface | null =
    hasSource && transform && transform.valid
      ? { transform, width: viewport.width, height: viewport.height, source, frozen: frozenFrame != null, frameId }
      : null

  return (
    <div ref={rootRef} className="cl:absolute cl:inset-0 cl:z-[5] cl:pointer-events-none cl:select-none">
      {frozenFrame && geometry && transform && transform.valid && (
        <FrozenFrameCanvas
          frame={frozenFrame}
          geometry={geometry}
          height={measurement.overlayHeight}
          transform={transform}
          width={measurement.overlayWidth}
        />
      )}
      {surface && (
        <>
          <PointerLayer active={p.interaction === "pointer"} surface={surface} />
          {p.children?.(surface)}
        </>
      )}
    </div>
  )
}

/**
 * The shared frozen canonical image (D-07): drawn on a canvas with the same transform
 * (translate to content-rect center, mirror, rotate) that pointers and shapes use, so nothing
 * drifts against them. Nobody's local video is paused - the live stage stays mounted underneath,
 * this canvas simply covers it while a frame is active.
 */
function FrozenFrameCanvas(p: {
  frame: SyncFrameDescriptorPayload
  geometry: FrameGeometry
  transform: ViewportTransform
  width: number
  height: number
}) {
  const sync = useRoomSync()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading")

  const draw = useCallback(
    (img: HTMLImageElement) => {
      const canvas = canvasRef.current
      if (!canvas || p.width <= 0 || p.height <= 0) {
        return
      }
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.round(p.width * dpr)
      canvas.height = Math.round(p.height * dpr)
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        return
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.fillStyle = "#000"
      ctx.fillRect(0, 0, p.width, p.height)

      const { contentRect, scale } = p.transform
      const cx = contentRect.x + contentRect.width / 2
      const cy = contentRect.y + contentRect.height / 2
      const sw = p.geometry.sourceW * scale
      const sh = p.geometry.sourceH * scale

      ctx.save()
      ctx.translate(cx, cy)
      if (p.geometry.mirrored) {
        ctx.scale(-1, 1)
      }
      ctx.rotate((p.geometry.rotation * Math.PI) / 180)
      ctx.drawImage(img, -sw / 2, -sh / 2, sw, sh)
      ctx.restore()
    },
    [p.geometry, p.height, p.transform, p.width],
  )

  // Depend on the resolved image source string, not the whole `sync` object (WR-01): a pointer
  // fade tick or any other unrelated RoomSyncProvider re-render must never retrigger this load.
  const frameImageSrc = sync.frameImageSrc(p.frame.frameId)

  useEffect(() => {
    let cancelled = false
    setStatus("loading")
    const img = new Image()
    img.onload = () => {
      if (cancelled) {
        return
      }
      setStatus("loaded")
      draw(img)
    }
    img.onerror = () => {
      if (cancelled) {
        return
      }
      setStatus("error")
    }
    img.src = frameImageSrc
    return () => {
      cancelled = true
    }
  }, [draw, frameImageSrc])

  return (
    <div className="cl:absolute cl:inset-0 cl:pointer-events-none">
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="cl:absolute cl:inset-0"
        style={{ width: `${p.width}px`, height: `${p.height}px` }}
      />
      {status === "loading" && (
        <div className="cl:absolute cl:inset-0 cl:flex cl:items-center cl:justify-center cl:text-xs cl:text-white/70">
          Loading frozen frame...
        </div>
      )}
      {status === "error" && (
        <div className="cl:absolute cl:inset-0 cl:flex cl:items-center cl:justify-center cl:text-xs cl:text-white/70">
          Could not load the frozen frame
        </div>
      )}
    </div>
  )
}

export default SyncStageOverlay
